import Cookies from "js-cookie";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import {
  TWO_FACTOR_DEVICE_TOKEN_KEY,
  TWO_FACTOR_RETURN_PATH_KEY,
  TWO_FACTOR_TEMP_TOKEN_KEY,
} from "@/two-factor/constants";
import { getToken, removeToken } from "./trpc";

export { AUTH_SESSION_CHANGED_EVENT, notifyAuthSessionChanged } from "./authSessionEvents";

export function readAuthToken(): string | null {
  return getToken() ?? null;
}

const LOGIN_FAILURE_MESSAGES = new Set(["ERR_LOGIN_INVALID_PASSWORD", "ERR_LOGIN_INVALID_USER"]);

function readTrpcError(error: unknown): { code?: string; message: string } {
  const err = error as {
    data?: { code?: string; json?: { message?: string } };
    shape?: { message?: string };
    message?: string;
  };
  return {
    code: err?.data?.code,
    message:
      err?.data?.json?.message ||
      err?.shape?.message ||
      err?.message ||
      "Something went wrong",
  };
}

/**
 * True when the server rejected the current portal session (soft delete, logout invalidation, etc.).
 * Does not include wrong-password login attempts (no token or known login error messages).
 */
export function isInvalidSessionTrpcError(error: unknown): boolean {
  const { code, message } = readTrpcError(error);
  if (code !== "UNAUTHORIZED") {
    return false;
  }

  if (LOGIN_FAILURE_MESSAGES.has(message)) {
    return false;
  }

  return Boolean(getToken());
}

export function getInvalidSessionMessage(error: unknown): string {
  return readTrpcError(error).message;
}

/** Removes portal auth cookie and related client-side session artifacts. */
export function invalidateClientSession(): void {
  removeToken();
  Cookies.remove(COOKIE_KEYS.refreshToken, { path: "/" });

  try {
    sessionStorage.removeItem(TWO_FACTOR_TEMP_TOKEN_KEY);
    sessionStorage.removeItem(TWO_FACTOR_RETURN_PATH_KEY);
  } catch {
    /* ignore */
  }

  try {
    localStorage.removeItem(TWO_FACTOR_DEVICE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
